/**
 * Announces which panel of a certificate configurator the client is filling.
 *
 * The energy certificates are the longest forms in the flow, and legacy's
 * `opens_*` events are the only place it measured progress inside a step. They
 * fired on each panel component's own mount; here they fire on selection
 * instead, which is the same moment for a configurator that mounts one panel at
 * a time and the only correct moment for one that keeps all its panels mounted
 * and toggles their visibility. Both shapes exist in this tree, which is why the
 * rule cannot live in the panels. A configurator on a hidden tab announces
 * nothing until its tab is shown.
 */

import { useEffect } from 'react'

import type { CartItemId, ConfigurationFlowId } from 'models/purchaseFlowRevamp'
import type { ConfigPanelId } from '../analytics/events'
import { PfRevampEventName } from '../analytics/events'
import { dispatchPfRevampEvent } from '../analytics/usePfRevampAnalytics'
import { useIsConfigPanelActive } from './sourceVisuals/configPanelActive'

export const useConfigPanelAnnouncement = (
  cartItemId: CartItemId,
  flowId: ConfigurationFlowId,
  panel: ConfigPanelId
): void => {
  const isActive = useIsConfigPanelActive()

  useEffect(() => {
    if (!isActive) return
    dispatchPfRevampEvent({
      type: PfRevampEventName.CONFIG_PANEL_OPENED,
      payload: { cartItemId, flowId, panel },
    })
  }, [isActive, cartItemId, flowId, panel])
}
